if (!customElements.get('samduo-nav-tabs')) {
  const AIM_DELAY = 120;
  const AIM_TOLERANCE = 40;
  const RESIZE_TIMING = { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SamduoNavTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.buttons = Array.from(this.querySelectorAll('[data-sd-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sd-panel]'));
      this.main = this.querySelector('.sd-nav__main');
      this.moves = [];
      this.addEventListener('mousemove', (event) => this.recordMove(event));
      this.addEventListener('mouseleave', () => {
        clearTimeout(this.aimTimer);
        this.aimTimer = null;
        this.moves = [];
        this.lastAimLoc = null;
      });
      this.buttons.forEach((button) => {
        // mouseenter fires before the mousemove for the same point, so record it here first
        button.addEventListener('mouseenter', (event) => {
          this.recordMove(event);
          this.hoverTab(button);
        });
        // The button's listener runs before the bubbling one on the host, so record the point here as well
        button.addEventListener('mousemove', (event) => {
          this.recordMove(event);
          if (!button.classList.contains('is-active') && !this.aimTimer) this.hoverTab(button);
        });
        button.addEventListener('focus', () => this.activate(button.dataset.sdTab));
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.activate(button.dataset.sdTab);
        });
      });
    }

    recordMove(event) {
      const last = this.moves[this.moves.length - 1];
      if (last && last.x === event.clientX && last.y === event.clientY) return;
      this.moves.push({ x: event.clientX, y: event.clientY, t: performance.now() });
      if (this.moves.length > 3) this.moves.shift();
    }

    hoverTab(button) {
      clearTimeout(this.aimTimer);
      this.aimTimer = null;
      if (button.classList.contains('is-active')) return;
      if (this.isAimingAtPanel()) {
        this.aimTimer = setTimeout(() => {
          this.aimTimer = null;
          if (button.matches(':hover')) this.hoverTab(button);
        }, AIM_DELAY);
        return;
      }
      this.activate(button.dataset.sdTab);
    }

    /* Menu-aim: while the pointer heads for the product panel, the categories it crosses on the way must not
       take over. "Heading for" = inside the triangle from the pointer to the panel's left edge (padded by
       AIM_TOLERANCE) and still moving; a pointer that stopped always wins. */
    isAimingAtPanel() {
      const loc = this.moves[this.moves.length - 1];
      const prev = this.moves[0];
      if (!this.main || !loc || !prev || loc === prev) return false;
      if (this.lastAimLoc && loc.x === this.lastAimLoc.x && loc.y === this.lastAimLoc.y) return false;

      const rect = this.main.getBoundingClientRect();
      if (loc.x >= rect.left || prev.x >= rect.left) return false;
      const upper = { x: rect.left, y: rect.top - AIM_TOLERANCE };
      const lower = { x: rect.left, y: rect.bottom + AIM_TOLERANCE };
      const slope = (a, b) => (b.y - a.y) / (b.x - a.x);

      const aiming = slope(loc, upper) < slope(prev, upper) && slope(loc, lower) > slope(prev, lower);
      this.lastAimLoc = aiming ? loc : null;
      return aiming;
    }

    activate(id) {
      if (!id) return;
      const resize = this.prepareResize();
      this.buttons.forEach((button) => {
        const on = button.dataset.sdTab === id;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-selected', on ? 'true' : 'false');
        button.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.sdPanel !== id;
      });
      resize();
    }

    /* The panels have different row counts: glide the menu height and the CTA row to the new panel instead
       of jumping. Measures the current (possibly mid-animation) state before the swap, so rapid switches
       continue from where the previous glide was. */
    prepareResize() {
      if (REDUCED_MOTION.matches || !this.offsetHeight) return () => {};
      const ctas = this.querySelector('.sd-nav__ctas');
      const fromHeight = this.getBoundingClientRect().height;
      const fromTop = ctas ? ctas.getBoundingClientRect().top : 0;

      return () => {
        if (this.heightAnim) this.heightAnim.cancel();
        if (this.ctasAnim) this.ctasAnim.cancel();
        this.heightAnim = null;
        this.ctasAnim = null;
        this.style.overflowY = '';

        const toHeight = this.getBoundingClientRect().height;
        if (Math.abs(toHeight - fromHeight) < 1) return;

        const anim = this.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], RESIZE_TIMING);
        this.heightAnim = anim;
        this.style.overflowY = 'clip';
        anim.onfinish = () => {
          if (this.heightAnim !== anim) return;
          this.heightAnim = null;
          this.style.overflowY = '';
        };

        if (ctas) {
          const shift = fromTop - ctas.getBoundingClientRect().top;
          if (shift) {
            this.ctasAnim = ctas.animate(
              [{ transform: `translateY(${shift}px)` }, { transform: 'translateY(0)' }],
              RESIZE_TIMING
            );
          }
        }
      };
    }
  }

  customElements.define('samduo-nav-tabs', SamduoNavTabs);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-sd-open-calculator]');
  if (!trigger) return;

  const modalId = trigger.getAttribute('aria-controls') || 'SavingsCalculator-nav';
  const modal = document.getElementById(modalId);
  if (!modal || typeof modal.show !== 'function') return;

  event.preventDefault();

  document.querySelectorAll('details[is="details-mega"][open]').forEach((details) => {
    details.open = false;
  });

  const drawer = document.querySelector('menu-drawer');
  if (drawer && typeof drawer.hide === 'function') {
    drawer.hide();
  }

  modal.show(trigger);
});

/* Mobile / tablet menu panel: hang it from the header bar (see .sd-menu in samduo-nav.css) */
(() => {
  const mobile = window.matchMedia('(max-width: 1023px)');

  const syncMenuPanel = () => {
    const drawer = document.getElementById('MenuDrawer');
    const header = document.querySelector('.header-section .header');
    if (!drawer || !header) return;
    const rect = header.getBoundingClientRect();
    drawer.style.setProperty('--sd-menu-top', `${Math.max(rect.top, 0).toFixed(2)}px`);
    drawer.style.setProperty('--sd-menu-head', `${rect.height.toFixed(2)}px`);
  };

  // Capture phase: runs before the theme's own click handler opens the drawer
  document.addEventListener(
    'click',
    (event) => {
      if (mobile.matches && event.target.closest('[aria-controls="MenuDrawer"]')) syncMenuPanel();
    },
    true
  );

  window.addEventListener('resize', () => {
    const drawer = document.getElementById('MenuDrawer');
    if (mobile.matches && drawer && drawer.hasAttribute('open')) syncMenuPanel();
  });
})();

/* ==========================================================================
   Desktop nav: Insta360-style open / close choreography

   The theme still owns the open state (details.open setter, Esc / focus-out /
   click-outside, sticky-header close, theme editor block select). This block only
   swaps the visuals for items marked data-sd-mega and adds the hover intent.
   Every other details-mega falls through to the theme's original animation.
   ========================================================================== */
(() => {
  if (window.__samduoNavDesktop) return;
  window.__samduoNavDesktop = true;

  const CLOSE_DELAY = 200; // ms the pointer may be away before the menu closes (hover intent)
  const NAV = '.header__navigation > .header__menu > ul.list-menu';
  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 1024px)');
  const state = {
    open: null, // details currently open
    active: null, // top-level <li> currently hovered / focused
    closeTimer: 0,
    finalizeTimer: 0,
    hover: false, // true while the pointer drives the menu: only then does leaving start the close delay
    switching: false,
    suppressFocusOpen: false,
    watching: false,
    raf: 0
  };

  const isTouch = () => Boolean(window.theme && theme.config && theme.config.isTouch);
  const hoverEnabled = () => desktop.matches && !isTouch();
  const navOf = (el) => (el && el.closest ? el.closest(NAV) : null);
  const overlayOf = () => document.querySelector('.header ~ .overlay');
  const summaryOf = (details) => details.querySelector(':scope > summary');
  const panelOf = (details) => details.querySelector(':scope > .mega-menu');
  const megaOf = (li) => li.querySelector(':scope > details[data-sd-mega]');

  const itemOf = (target) => (target && target.closest ? target.closest(`${NAV} > li`) : null);

  const activate = (li) => {
    state.active = li;
  };

  const clearActive = () => {
    state.active = null;
  };

  /* ---- layout sync while a panel is open ------------------------------ */

  const syncTop = () => {
    const header = state.open && state.open.closest('.header');
    if (!header) return;
    const rect = header.getBoundingClientRect();
    const border = parseFloat(getComputedStyle(header).borderBottomWidth) || 0;
    root.style.setProperty('--sd-nav-top', `${(rect.bottom - border).toFixed(2)}px`);
  };

  const onViewportChange = () => {
    if (state.raf) return;
    state.raf = requestAnimationFrame(() => {
      state.raf = 0;
      if (state.open) syncTop();
    });
  };

  const watch = (on) => {
    if (on === state.watching) return;
    state.watching = on;
    const method = on ? 'addEventListener' : 'removeEventListener';
    window[method]('scroll', onViewportChange, { passive: true });
    window[method]('resize', onViewportChange);
  };

  /* ---- layer open / close (driven by the details-mega hooks) ---------- */

  const cancelFinalize = () => {
    clearTimeout(state.finalizeTimer);
    state.finalizeTimer = 0;
  };

  // Hides the backdrop with no fade-out. Skipped while another panel is still open.
  const finalizeClose = () => {
    cancelFinalize();
    if (state.open) return;
    watch(false);
    if (!root.classList.contains('sd-nav-open')) return;
    const overlay = overlayOf();
    root.classList.add('sd-nav-closing');
    root.classList.remove('sd-nav-open');
    if (overlay) void overlay.offsetWidth; // flush while the transition is off
    root.classList.remove('sd-nav-closing');
  };

  // The theme closes via Esc / focus-out / click-outside without us: wait one task so a
  // focus-out immediately followed by the next item's focus-in counts as a switch.
  const scheduleFinalize = () => {
    if (!state.finalizeTimer) state.finalizeTimer = setTimeout(finalizeClose, 0);
  };

  const onOpened = (details) => {
    const previous = state.open;
    if (previous && previous !== details) {
      state.switching = true;
      try {
        previous.open = false;
      } finally {
        state.switching = false;
      }
    }

    const fresh = !root.classList.contains('sd-nav-open');
    // The theme has just switched its own 0.8s overlay on. Keep it hidden until our fade starts, otherwise
    // the first style flush below would start the slow theme transition and our 200ms one would never run.
    if (fresh) root.classList.add('sd-nav-pending');

    try {
      cancelFinalize();
      state.open = details;
      activate(details.closest('li'));

      const panel = panelOf(details);
      const summary = summaryOf(details);
      if (summary) summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('with-mega');
      syncTop();
      watch(true);
      if (!panel) return;

      const container = panel.firstElementChild;
      if (container) container.scrollTop = 0;
      panel.classList.remove('is-shown', 'is-in', 'is-instant');
      // Switching: the panel appears at full opacity straight away (no new fade).
      if (!fresh) panel.classList.add('is-shown', 'is-instant');
      void panel.offsetWidth; // render the "from" state once so the transitions have something to start from
      if (fresh) {
        root.classList.remove('sd-nav-closing');
        root.classList.add('sd-nav-open'); // backdrop and panel start fading in the same style change
        panel.classList.add('is-shown');
      }
      panel.classList.add('is-in'); // content stagger (replays on every show, including item switches)
    } finally {
      root.classList.remove('sd-nav-pending');
    }
  };

  const onClosed = (details) => {
    const panel = panelOf(details);
    const summary = summaryOf(details);
    if (panel) panel.classList.remove('is-shown', 'is-in', 'is-instant');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('with-mega');
    if (state.open === details) state.open = null;
    if (state.active && state.active === details.closest('li')) clearActive();
    if (!state.switching) scheduleFinalize();
  };

  /* ---- hover intent ---------------------------------------------------- */

  const cancelClose = () => {
    clearTimeout(state.closeTimer);
    state.closeTimer = 0;
  };

  const closeNow = () => {
    cancelClose();
    const details = state.open;
    if (details) details.open = false;
    finalizeClose();
  };

  const closeAll = () => {
    state.hover = false;
    clearActive();
    closeNow();
  };

  // Only a pointer-driven menu closes on leave. A menu opened from the keyboard must not close because the
  // (resting) pointer happens to sit over the page, which the browser reports as a fresh mouseover on the backdrop.
  const scheduleClose = () => {
    if (state.closeTimer || !state.hover || (!state.open && !state.active)) return;
    state.closeTimer = setTimeout(closeAll, CLOSE_DELAY);
  };

  const enter = (li, viaPointer = true) => {
    cancelClose();
    if (viaPointer) state.hover = true;
    const details = megaOf(li);
    if (state.active !== li) {
      activate(li);
      // an item without a submenu hides the open panel immediately, no delay
      if (!details && state.open) closeNow();
    }
    if (details && !details.open) details.open = true;
  };

  const hoverModeOf = (el) => {
    const nav = navOf(el);
    return Boolean(nav) && nav.dataset.sdNav === 'hover';
  };

  const onMouseOver = (event) => {
    if (!hoverEnabled()) return;
    const target = event.target;
    const li = itemOf(target);
    if (li) {
      if (hoverModeOf(li)) enter(li);
    } else if (target.matches && target.matches(NAV) && hoverModeOf(target)) {
      // the gap between two items still belongs to the nav: keep whatever is open
      cancelClose();
    } else {
      scheduleClose();
    }
  };

  const onPointerLeavesWindow = () => {
    if (hoverEnabled()) scheduleClose();
  };

  /* ---- keyboard + click ------------------------------------------------ */

  const onFocusIn = (event) => {
    if (state.suppressFocusOpen || !hoverEnabled()) return;
    const target = event.target;
    if (!target.matches || !target.matches('summary, a.menu__item') || !target.matches(':focus-visible')) return;
    const li = itemOf(target);
    if (li && hoverModeOf(li)) enter(li, false);
  };

  // A plain item that was focused from the keyboard has no panel whose close would reset the active item.
  const onFocusOut = (event) => {
    if (!state.active || state.hover || state.open) return;
    const next = event.relatedTarget;
    if (!next || !itemOf(next)) clearActive();
  };

  const onKeyDown = (event) => {
    if (event.key !== 'Escape' || !state.open) return;
    const details = state.open;
    const summary = summaryOf(details);
    const focusInside = details.contains(document.activeElement) && document.activeElement !== summary;
    closeAll();
    if (focusInside && summary) {
      state.suppressFocusOpen = true;
      summary.focus();
      state.suppressFocusOpen = false;
    }
  };

  // Hover mode: clicking a top-level item follows its link (same as the theme's hover trigger).
  // Touch / click mode falls through to the theme's own toggle.
  const onClickCapture = (event) => {
    const summary = event.target.closest ? event.target.closest(`${NAV} > li > details[data-sd-mega] > summary`) : null;
    if (!summary || !hoverEnabled() || !hoverModeOf(summary)) return;
    const href = summary.getAttribute('data-link');
    if (!href) return;
    event.preventDefault();
    event.stopPropagation();
    window.location.href = href;
  };

  /* ---- lifecycle -------------------------------------------------------- */

  const initAria = () => {
    document.querySelectorAll(`${NAV} > li > details[data-sd-mega] > summary`).forEach((summary) => {
      summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false');
    });
  };

  const hardReset = () => {
    cancelClose();
    cancelFinalize();
    state.hover = false;
    const details = state.open;
    state.open = null;
    state.switching = true;
    if (details && details.isConnected && details.open) details.open = false;
    state.switching = false;
    clearActive();
    finalizeClose(); // backdrop off with no fade, also stops the scroll / resize watchers
    root.classList.remove('sd-nav-open', 'sd-nav-closing', 'sd-nav-pending');
  };

  const patchDetailsMega = () => {
    const DetailsMega = customElements.get('details-mega');
    if (!DetailsMega) return false;
    const proto = DetailsMega.prototype;
    if (!proto.__samduoNav) {
      const transitionIn = proto.transitionIn;
      const transitionOut = proto.transitionOut;
      proto.transitionIn = function () {
        if (!this.hasAttribute('data-sd-mega')) return transitionIn.call(this);
        onOpened(this);
        return Promise.resolve();
      };
      proto.transitionOut = function () {
        if (!this.hasAttribute('data-sd-mega')) return transitionOut.call(this);
        onClosed(this);
        return Promise.resolve();
      };
      proto.__samduoNav = true;
    }
    return true;
  };

  let booted = false;

  const init = () => {
    if (booted || !patchDetailsMega()) return;
    booted = true;
    root.classList.add('sd-nav-js');
    initAria();

    document.addEventListener('mouseover', onMouseOver);
    root.addEventListener('mouseleave', onPointerLeavesWindow);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClickCapture, true);
    desktop.addEventListener('change', (event) => {
      if (!event.matches) hardReset();
    });
    ['shopify:section:load', 'shopify:section:unload'].forEach((name) => {
      document.addEventListener(name, () => {
        hardReset();
        initAria();
      });
    });
  };

  init(); // theme.js is deferred earlier in <head>, so details-mega is normally defined already
  customElements.whenDefined('details-mega').then(init);
})();
